import { T } from "@/lib/i18n";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, BellOff, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const toKey = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const PushToggle = ({ dark }) => {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!supported()) return;
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, []);
  const cls = `inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium border transition-colors disabled:opacity-50 ${dark
    ? "border-[#27272a] text-[#a1a1aa] hover:border-white hover:text-white" : "border-[#cfd6d9] text-[#2B3036] hover:border-[#2B3036]"}`;
  if (!supported()) {
    if (!isIOS() || standalone()) return null;
    return <span data-testid="push-ios-hint" className="text-[11px] text-[#71717a]">{T("Installa l'app dalla schermata Home per ricevere le notifiche")}</span>;
  }
  const enable = async () => {
    setBusy(true);
    try {
      if (await Notification.requestPermission() !== "granted") { toast.error(T("Permesso notifiche negato dal browser")); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) });
      await axios.post(`${API}/push/subscribe`, s.toJSON());
      setSub(s);
      toast.success(T("Notifiche attivate su questo dispositivo"));
    } catch { toast.error(T("Impossibile attivare le notifiche su questo dispositivo")); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }); await sub.unsubscribe(); setSub(null); toast.success(T("Notifiche disattivate")); }
    finally { setBusy(false); }
  };
  return sub ? (
    <button data-testid="push-disable" onClick={disable} disabled={busy} className={cls}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellOff className="w-3.5 h-3.5" />} Disattiva notifiche
    </button>
  ) : (
    <button data-testid="push-enable" onClick={enable} disabled={busy} className={cls}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Attiva notifiche sul telefono
    </button>
  );
};
